import React from 'react';
import Joi from 'joi';
import _ from 'lodash';

import InspectionStandard from '../../themed/inspection-standard';
import Form from '../../common/form';
import VICustomCoverItemForm from '../visual-inspection/viCustomCoverItemForm';
import VICustomCoverItemEditForm from '../visual-inspection/viCustomCoverItemEditForm';

import withRouter from '../../hoc/withRouter';

import { SelectOption } from '../../../utils/models';
import { matchPhrases } from '../../../utils/utilities';
import toastService, { error } from '../../../services/toastService';

class CustomCoverForm extends Form {
	state = {
		data: {
			workOrderNumber: '',
			scopeOfWork: '',
			exxonMobilWorkProcedure: '',
			vtProcedure: '',
			ropeAccessCodeOfPractice: '',
			russelsmithProcedure: '',
			coverData: [],
		},
		errors: {},
		loading: false,
		touched: false,
		progress: 0,
		selectedInspectionItem: null,
	};

	schema = {
		workOrderNumber: Joi.string().optional().label('Work Order Number'),
		exxonMobilWorkProcedure: Joi.string()
			.min(3)
			.required()
			.label('ExxonMobil Work Procedure'),
		vtProcedure: Joi.string().min(3).required().label('VT Procedure'),
		ropeAccessCodeOfPractice: Joi.string()
			.min(3)
			.required()
			.label('Rope Access Code of Practice'),
		scopeOfWork: Joi.string().min(5).required().label('Scope Of Work'),
		russelsmithProcedure: Joi.string().optional().label('Arridex Procedure'),
		coverData: Joi.array()
			.min(1)
			.items(
				Joi.object({
					id: Joi.string().required().label('Inspection Standard Label'),
					name: Joi.string().min(3).required().label('Inspection Standard Value'),
				})
			)
			.required()
			.label('Cover Data'),
	};
	
	componentDidMount(){
		if (this.props.initialData) {
			this.extractCountFromCoverData();
		}
	}

	componentDidUpdate(prevProps: Readonly<any>): void {
		if (!this.state.touched) {
			if (!_.isEqual(prevProps.initialData, this.props.initialData)) {
				this.extractCountFromCoverData();
			}
		}
	}

	extractCountFromCoverData = () => {
		type CoverDataItem = { id: string; name: string; count: number };
		
		const initialData = { ...this.props.initialData, russelsmithProcedure: this.props.initialData.russelsmithProcedure ?? '' };
		initialData.coverData = initialData.coverData.map((coverDataItem: CoverDataItem) => _.omit(coverDataItem, ['count']));

		this.setState({ data: initialData });
	}

	handleInspectionItemSelect = (item: SelectOption | null) => {
		this.setState({ selectedInspectionItem: item } as any);
	};

	handleAddCoverItem = (coverItem: SelectOption) => {
		const allCoverItems: SelectOption[] = this.state.data.coverData;
		const coverItemMatch = allCoverItems.find((item) => matchPhrases(item.id.toString(), coverItem.id.toString()));
		if (coverItemMatch) return error('This inspection standard has already been added!');

		const newCoverItem: SelectOption = { id: coverItem.id, name: coverItem.name };
		this.setState({ data: { ...this.state.data, coverData: [...allCoverItems, newCoverItem] } });
	};

	handleEditCoverItem = (coverItem: SelectOption) => {
		const { selectedInspectionItem } = this.state
		const newCoverItems = this.state.data.coverData.map((item: SelectOption) => item.id === (selectedInspectionItem as unknown as SelectOption)!.id ? coverItem : item);

		this.setState({ data: { ...this.state.data, coverData: newCoverItems } });
		this.handleInspectionItemSelect(null);
	};

	handleRemoveCoverItem = (coverItem: SelectOption) => {
		const newCoverData = this.state.data.coverData.filter((item: SelectOption) => item.id !== coverItem.id);
		this.setState({ data: { ...this.state.data, coverData: newCoverData } });
	};

	doSubmit = () => {
		this.props.onAddCoverForm(this.state.data);
		toastService.success('Cover data updated!');
	};

	render() {
		const { data, selectedInspectionItem } = this.state;

		return (
			<React.Fragment>
				<div className="my-4 divider"></div>

				<VICustomCoverItemForm onAddCoverItem={this.handleAddCoverItem} />

				{data.coverData.map((item: SelectOption) =>
					item.id === (selectedInspectionItem as any)?.id ? (
						<VICustomCoverItemEditForm
							key={item.id}
							initialCoverItem={item}
							onEditCoverItem={this.handleEditCoverItem}
							onCancelEdit={() => this.handleInspectionItemSelect(null)}
						/>
					) : (
						<InspectionStandard
							key={item.id}
							title={item.id as string}
							body={item.name as string}
							onRemove={() => this.handleRemoveCoverItem(item)}
							onEdit={() => this.handleInspectionItemSelect(item)}
						/>
					)
				)}

				<div className="my-4 divider"></div>

				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<p className="form-title">Reference Documents / Work Instruction</p>

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'workOrderNumber',
								label: 'Work Order Number',
								placeholder: 'Enter Work Order Number',
							})}
						</div>
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'scopeOfWork',
								label: 'Scope of work',
								placeholder: 'Enter Scope of Work',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					{this.renderInput({
						type: 'text',
						name: 'ropeAccessCodeOfPractice',
						label: 'Rope Access Code of Practice',
						placeholder: 'Enter Rope Access Code of Practice',
					})}

					<div className="my-4 divider"></div>

					<div className="row">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'vtProcedure',
								label: 'VT Procedure',
								placeholder: 'Enter VT Procedure',
							})}
						</div>
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'exxonMobilWorkProcedure',
								label: 'Work Procedure',
								placeholder: 'Enter Work Procedure',
							})}
						</div>
					</div>

					<div className="my-4 divider"></div>

					{this.renderInput({
						type: 'text',
						name: 'russelsmithProcedure',
						label: 'Arridex Procedure',
						placeholder: 'Enter Arridex Procedure',
					})}

					<div className="my-4 divider"></div>

					<div className="d-flex justify-content-end">
						{this.renderButton('Save Cover Sheet Data')}
					</div>
				</form>
			</React.Fragment>
		);
	}
}

export default withRouter(CustomCoverForm);
